import React from 'react'

interface HelpModalProps {
  isOpen: boolean
  onClose: () => void
}

export function HelpModal({ isOpen, onClose }: HelpModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">How to Use Flashcard SRS</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <section>
            <h3 className="text-lg font-semibold mb-2">Study Mode</h3>
            <p className="mb-2">Study cards using spaced repetition. The app automatically rotates through your cards based on their review intervals.</p>
            
            <h4 className="font-semibold mt-3 mb-1">Keyboard Shortcuts:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li><kbd className="bg-gray-200 px-2 py-1 rounded">Space</kbd> - Show back of card</li>
              <li><kbd className="bg-gray-200 px-2 py-1 rounded">A</kbd> - Mark as mastered (removes from rotation)</li>
              <li><kbd className="bg-gray-200 px-2 py-1 rounded">S</kbd> - Mark as recalled (increases interval)</li>
              <li><kbd className="bg-gray-200 px-2 py-1 rounded">D</kbd> - Mark as missed (resets interval)</li>
              <li><kbd className="bg-gray-200 px-2 py-1 rounded">H</kbd> - Hide card (removes from rotation)</li>
              <li><kbd className="bg-gray-200 px-2 py-1 rounded">T</kbd> - Toggle translations</li>
              <li><kbd className="bg-gray-200 px-2 py-1 rounded">Z</kbd> - Undo last action (go back to previous card)</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Cards Tab</h3>
            <p className="mb-2">View, create, and manage your flashcards. You can:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Create individual cards or batch import multiple cards</li>
              <li>Edit card content, tags, and language</li>
              <li>Select multiple cards to update tags or language in bulk</li>
              <li>Filter cards by exercise</li>
              <li>Search cards by content</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Card Syntax</h3>
            <p className="mb-2">Use special syntax when creating cards:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><code className="bg-gray-200 px-1">%text%</code> - Text that appears only when translations are shown</li>
              <li><code className="bg-gray-200 px-1">$text$</code> - Text that is spoken by text-to-speech</li>
              <li>Underscores (_) in TTS text are pronounced as "blanco" (blank)</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Settings</h3>
            <p>Configure:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Text-to-speech voice selection</li>
              <li>Maximum number of cards in study rotation</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Tips</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Use exercises to organize related cards into study groups</li>
              <li>Cards are prioritized by how close they are to being mastered</li>
              <li>The app continuously rotates cards based on SRS intervals</li>
              <li>Press <kbd className="bg-gray-200 px-2 py-1 rounded">?</kbd> anywhere to show this help</li>
            </ul>
          </section>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}